/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useEffect, useMemo} from 'react'
import {detectInIframe, isRefObject} from '../utils/utils'
import {useInView} from 'react-intersection-observer'

/**
 * Returns a boolean to indicate if an element is visible on the screen. Fall back to `true`
 * if IntersectionObserver is not supported.
 * https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver
 * @param {Object} [options]
 * @param {Object} [options.root] - See IntersectionObserver options
 * @param {string} [options.rootMargin] - See IntersectionObserver options
 * @param {number|Array<number>} [options.threshold] - See IntersectionObserver options
 * @param {boolean} [options.useOnce] - Detach the observer after the element appears.
 * @param {boolean} [options.skip] - Skip the observer if true.
 * @param {boolean} [options.initialInView] - Initial in view state.
 * @param {Object|Element} [ref] - element ref - it's better to use the returned setRef function
 * @returns {[function, boolean]} - A function to set the ref and a boolean indicating if the element is in view.
 *
 */
const useIntersectionObserver = (options = {}, ref) => {
    const root = useMemo(() => {
        // Inside cross-origin iframes, root margin doesn't work unless a root element is provided.
        // ref: https://www.w3.org/TR/intersection-observer/#dom-intersectionobserver-rootmargin
        if (options.rootMargin && !options.root && detectInIframe()) {
            return document.querySelector(':root')
        }

        return options.root
    }, [options.root, options.rootMargin])

    const {useOnce, ...ioOptions} = options

    const [setRef, isIntersecting] = useInView({
        triggerOnce: useOnce,
        fallbackInView: true,
        initialInView: undefined,
        ...ioOptions,
        // replace with our computed root to handle iframes
        root
    })

    useEffect(() => {
        if (ref) {
            const targetElement = isRefObject(ref) ? ref.current : ref
            setRef(targetElement)
        }
    }, [ref, setRef])

    return [setRef, isIntersecting]
}

export default useIntersectionObserver
